import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FiSearch as Search, FiActivity as Activity, FiFilter as Filter, FiRefreshCw as RefreshCw, FiClock as Clock, FiCheckCircle as CheckCircle, FiXCircle as XCircle,
  FiAlertTriangle as AlertTriangle, FiChevronRight as ChevronRight, FiList as List, FiHash as Hash, FiUser as User, FiCalendar as Calendar,
  FiZap as Zap, FiDollarSign as DollarSign, FiCpu as Cpu, FiTrendingUp as TrendingUp, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp,
  FiChevronLeft as ChevronLeft, FiRefreshCcw as ArrowUpDown, FiCpu as Bot,
  FiTrash2 as TrashIcon, FiSquare as Square, FiCheckSquare as CheckSquare
} from 'react-icons/fi';
import {
  getTraces, getSessions, bulkDeleteTraces, bulkDeleteSessions, bulkAssignTraceProject, bulkAssignSessionProject,
  bulkAssignTraceWorkload, bulkAssignSessionWorkload
} from '../../../../services/aiOperationsApi';
import { useProject } from '../../../../contexts/ProjectContext';
import { useFilter } from '../../../../contexts/FilterContext';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';

// Tab configuration
const TABS = [
  { id: 'traces', label: 'Traces', icon: Activity },
  { id: 'sessions', label: 'Sessions', icon: List }
];

// Pagination Component
const PaginationControls = ({ page, totalPages, onPageChange, totalItems, pageSize }: { page: number; totalPages: number; onPageChange: (page: number) => void; totalItems: number; pageSize: number }) => (
  <div className="flex items-center justify-between px-6 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
    <div className="text-sm text-gray-600 dark:text-gray-400">
      Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalItems)} of {totalItems}
    </div>
    <div className="flex items-center gap-4">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-4 h-4" />
        Previous
      </button>
      <span className="text-sm text-gray-600 dark:text-gray-400">
        Page {page} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  </div>
);

// Types
interface TraceItem {
  id: string;
  agent_name?: string;
  name?: string;
  task?: string;
  status?: string;
  outcome?: string;
  duration_ms?: number;
  duration_seconds?: number;
  latency_ms?: number;
  total_tokens?: number;
  input_tokens?: number;
  output_tokens?: number;
  total_cost?: number;
  cost?: number;
  created_at?: string;
  timestamp?: string;
  model_id?: string;
  model?: string;
  user_id?: string;
  session_id?: string;
  aggregated_tracking?: boolean;
  project_id?: string | null;
  workload_id?: string | null;
  [key: string]: unknown;
}

interface SessionItem {
  id?: string;
  session_id?: string;
  user_id?: string;
  agent_name?: string;
  trace_count?: number;
  message_count?: number;
  total_duration_ms?: number;
  duration_ms?: number;
  total_cost?: number;
  cost?: number;
  total_tokens?: number;
  model?: string;
  created_at?: string;
  started_at?: string;
  first_trace_at?: string;
  last_trace_at?: string;
  project_id?: string | null;
  workload_id?: string | null;
  [key: string]: unknown;
}

interface DateRangeState {
  preset: string;
  startDate: string;
  endDate: string;
}

type TraceStatusKey = 'success' | 'completed' | 'error' | 'failed' | 'failure' | 'degraded' | 'running' | 'pending' | 'partial_success' | 'cancelled' | 'retrying' | 'timeout';

// Traces Tab Content
const TracesContent = ({ onTraceClick, sharedDateRange, onDateRangeChange }: { onTraceClick: (id: string) => void; sharedDateRange: DateRangeState | null; onDateRangeChange: (range: DateRangeState) => void }) => {
  const { selectedProjectId, projects } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [traces, setTraces] = useState<TraceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Basic filters - outcome is the main filter (matches what badge shows)
  const [filters, setFilters] = useState({
    outcome: '',
    search: ''
  });

  // Advanced filters - use shared date range from parent for persistence
  const [advancedFilters, setAdvancedFilters] = useState({
    userId: '',
    sessionId: '',
    agentName: '',
    status: '',
    startDate: sharedDateRange?.startDate || '',
    endDate: sharedDateRange?.endDate || '',
    promptName: '',
    promptId: '',
    datasetId: ''
  });

  // Sync advanced filters with shared date range when it changes
  useEffect(() => {
    if (sharedDateRange) {
      setAdvancedFilters(prev => ({
        ...prev,
        startDate: sharedDateRange.startDate || '',
        endDate: sharedDateRange.endDate || ''
      }));
    }
  }, [sharedDateRange]);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const loadTraces = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        page: page,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      // Basic filters - outcome is the main filter (matches what badge shows)
      if (filters.outcome) params.outcome = filters.outcome;

      // Advanced filters - aligned with backend API
      if (advancedFilters.userId) params.user_id = advancedFilters.userId;
      if (advancedFilters.sessionId) params.session_id = advancedFilters.sessionId;
      if (advancedFilters.agentName) params.agent_name = advancedFilters.agentName;
      if (advancedFilters.status) params.status = advancedFilters.status;
      if (advancedFilters.startDate) params.start_date = advancedFilters.startDate;
      if (advancedFilters.endDate) params.end_date = advancedFilters.endDate;
      if (advancedFilters.promptName) params.prompt_name = advancedFilters.promptName;
      if (advancedFilters.promptId) params.prompt_id = advancedFilters.promptId;
      if (advancedFilters.datasetId) params.dataset_id = advancedFilters.datasetId;

      // Project filter from ProjectContext
      if (selectedProjectId) params.project_id = selectedProjectId;

      const data = await getTraces(params);
      setTraces(data.data || data || []);
      const total = data.meta?.total || data.total || data.count || (data.data || data || []).length;
      setTotalItems(total);
      setTotalPages(data.meta?.total_pages || Math.ceil(total / pageSize));
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : String(err)) || 'Failed to load traces');
    } finally {
      setLoading(false);
    }
  }, [
    filters.outcome,
    advancedFilters.userId,
    advancedFilters.sessionId,
    advancedFilters.agentName,
    advancedFilters.status,
    advancedFilters.startDate,
    advancedFilters.endDate,
    advancedFilters.promptName,
    advancedFilters.promptId,
    advancedFilters.datasetId,
    selectedProjectId,
    filterType,
    filterId,
    page,
    pageSize,
    sortBy,
    sortOrder
  ]);

  useEffect(() => {
    loadTraces();
  }, [loadTraces]);

  const applyAdvancedFilters = () => {
    setPage(1);
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      userId: '',
      sessionId: '',
      agentName: '',
      status: '',
      startDate: '',
      endDate: '',
      promptName: '',
      promptId: '',
      datasetId: ''
    });
    setPage(1);
  };

  const filteredTraces = traces.filter(trace => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      trace.agent_name?.toLowerCase().includes(searchLower) ||
      trace.name?.toLowerCase().includes(searchLower) ||
      trace.id?.toLowerCase().includes(searchLower) ||
      trace.task?.toLowerCase().includes(searchLower)
    );
  });

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filteredTraces.length ? new Set() : new Set(filteredTraces.map((x) => x.id))
    );
  }, [filteredTraces]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!globalThis.confirm(`Delete ${count} trace${count > 1 ? 's' : ''}? They will be excluded from the list and metrics.`)) return;
    try {
      await bulkDeleteTraces(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadTraces();
    } catch (error: unknown) {
      console.error('Error deleting traces:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      success: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-300', icon: CheckCircle, label: 'Success' },
      completed: { bg: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-800 dark:text-green-300', icon: CheckCircle, label: 'Completed' },
      error: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300', icon: XCircle, label: 'Error' },
      failed: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300', icon: XCircle, label: 'Failed' },
      failure: { bg: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-800 dark:text-red-300', icon: XCircle, label: 'Failed' },
      degraded: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-800 dark:text-amber-300', icon: AlertTriangle, label: 'Degraded' },
      running: { bg: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-800 dark:text-blue-300', icon: RefreshCw, label: 'Running' },
      pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-800 dark:text-yellow-300', icon: Clock, label: 'Pending' },
      partial_success: { bg: 'bg-amber-100 dark:bg-amber-900/50', text: 'text-amber-800 dark:text-amber-300', icon: AlertTriangle, label: 'Partial Success' },
      cancelled: { bg: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-800 dark:text-orange-300', icon: XCircle, label: 'Cancelled' },
      retrying: { bg: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-800 dark:text-purple-300', icon: RefreshCw, label: 'Retrying' },
      timeout: { bg: 'bg-rose-100 dark:bg-rose-900/50', text: 'text-rose-800 dark:text-rose-300', icon: Clock, label: 'Timeout' }
    };

    const config = statusConfig[status as TraceStatusKey] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className={`w-3.5 h-3.5 ${status === 'running' || status === 'retrying' ? 'animate-spin' : ''}`} />
        {config.label}
      </span>
    );
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms && ms !== 0) return 'N/A';
    if (ms < 1000) {
      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(ms)}ms`;
    }
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getProjectName = (projectId: string | null | undefined): string | null => {
    if (!projectId) return null;
    return projects.find(p => p.id === projectId)?.name ?? null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  // Calculate stats for summary cards
  const totalTraces = traces.length;
  const successCount = traces.filter(t =>
    ['success', 'partial_success'].includes(t.outcome as string) || (!t.outcome && t.status === 'completed')
  ).length;
  const errorCount = traces.filter(t =>
    ['error', 'failure', 'timeout'].includes(t.outcome as string) || t.status === 'failed'
  ).length;
  const avgLatency = traces.length > 0
    ? traces.reduce((sum, t) => sum + ((t.duration_seconds || 0) * 1000) + (t.duration_ms || t.latency_ms || 0), 0) / traces.length
    : 0;
  const errorRate = totalTraces > 0 ? (errorCount / totalTraces) * 100 : 0;

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Traces</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalTraces)}
              </p>
            </div>
            <Activity className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Latency</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatDuration(avgLatency)}
              </p>
            </div>
            <Zap className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Success Rate</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {totalTraces > 0 ? ((successCount / totalTraces) * 100).toFixed(1) : 0}%
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Error Rate</p>
              <p className={`text-2xl font-bold mt-1 ${errorRate > 5 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                {errorRate.toFixed(1)}%
              </p>
            </div>
            <AlertTriangle className={`w-8 h-8 ${errorRate > 5 ? 'text-red-600' : 'text-gray-400'}`} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {/* Basic Filters Row */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center gap-4">
            <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation" className="flex items-center">
              <button
                onClick={toggleSelectAll}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {selectedIds.size === filteredTraces.length && filteredTraces.length > 0
                  ? <CheckSquare className="w-5 h-5 text-blue-600" />
                  : <Square className="w-5 h-5 text-gray-400" />
                }
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search traces..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <select
              value={filters.outcome}
              onChange={(e) => {
                setFilters({ ...filters, outcome: e.target.value });
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Outcomes</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="timeout">Timeout</option>
              <option value="partial_success">Partial Success</option>
              <option value="cancelled">Cancelled</option>
              <option value="warning">Warning</option>
            </select>

            {/* Sort Controls */}
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="created_at">Timestamp</option>
                <option value="cost">Cost</option>
                <option value="duration_seconds">Latency</option>
                <option value="input_tokens">Input Tokens</option>
                <option value="output_tokens">Output Tokens</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
            </div>

            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showAdvancedFilters
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Advanced
            </button>
            <button
              onClick={loadTraces}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900/50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date Range */}
              <div>
                <label htmlFor="obs-start-date" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                <input
                                                                                  id="obs-start-date"
                  type="datetime-local"
                  value={advancedFilters.startDate}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setAdvancedFilters({ ...advancedFilters, startDate: newStart });
                    // Propagate to parent for persistence across tabs
                    onDateRangeChange?.({ preset: 'custom', startDate: newStart, endDate: advancedFilters.endDate });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label htmlFor="obs-end-date" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                <input
                                                                                  id="obs-end-date"
                  type="datetime-local"
                  value={advancedFilters.endDate}
                  onChange={(e) => {
                    const newEnd = e.target.value;
                    setAdvancedFilters({ ...advancedFilters, endDate: newEnd });
                    // Propagate to parent for persistence across tabs
                    onDateRangeChange?.({ preset: 'custom', startDate: advancedFilters.startDate, endDate: newEnd });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* User & Session */}
              <div>
                <label htmlFor="obs-user-id" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">User ID</label>
                <input
                                                                                  id="obs-user-id"
                  type="text"
                  placeholder="Filter by user..."
                  value={advancedFilters.userId}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, userId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label htmlFor="obs-session-id" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Session ID</label>
                <input
                                                                                  id="obs-session-id"
                  type="text"
                  placeholder="Filter by session..."
                  value={advancedFilters.sessionId}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, sessionId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* Agent Name */}
              <div>
                <label htmlFor="obs-agent-name" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Agent Name</label>
                <input
                                                                                  id="obs-agent-name"
                  type="text"
                  placeholder="e.g., demo_agent"
                  value={advancedFilters.agentName}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, agentName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label htmlFor="obs-process-status" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Process Status</label>
                <select
                                                                                  id="obs-process-status"
                  value={advancedFilters.status}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="running">Running</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* GenAIOps Filters */}
              <div>
                <label htmlFor="obs-prompt-name" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Prompt Name</label>
                <input
                                                                                  id="obs-prompt-name"
                  type="text"
                  placeholder="Filter by prompt..."
                  value={advancedFilters.promptName}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, promptName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label htmlFor="obs-prompt-id" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Prompt ID</label>
                <input
                                                                                  id="obs-prompt-id"
                  type="text"
                  placeholder="Filter by prompt ID..."
                  value={advancedFilters.promptId}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, promptId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div>
                <label htmlFor="obs-dataset-id" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Dataset ID</label>
                <input
                                                                                  id="obs-dataset-id"
                  type="text"
                  placeholder="Filter by dataset..."
                  value={advancedFilters.datasetId}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, datasetId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </div>

            {/* Apply/Clear Buttons */}
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={applyAdvancedFilters}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <Filter className="w-4 h-4" />
                Apply Filters
              </button>
              <button
                onClick={clearAdvancedFilters}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={async (projectId) => {
                await bulkAssignTraceProject(Array.from(selectedIds), projectId);
                setTraces(prev => prev.map(t => selectedIds.has(t.id) ? { ...t, project_id: projectId } : t));
                setSelectedIds(new Set());
              }}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                const ids = Array.from(selectedIds);
                await bulkAssignTraceWorkload(ids, workloadId);
                setSelectedIds(new Set());
                loadTraces();
              }}
              size="sm"
            />
            <button
              type="button"
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Traces List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {filteredTraces.length === 0 ? (
          <div className="p-12 text-center">
            <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No traces found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTraces.map((trace) => (
                <div
                  key={trace.id}
                  onClick={() => onTraceClick(trace.id)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onTraceClick(trace.id); }} role="button" tabIndex={0}
                  className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                        <button
                          onClick={() => toggleSelect(trace.id)}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {selectedIds.has(trace.id)
                            ? <CheckSquare className="w-5 h-5 text-blue-600" />
                            : <Square className="w-5 h-5 text-gray-400" />
                          }
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {trace.agent_name || trace.name || trace.task?.slice(0, 50) || 'Unnamed Trace'}
                          </span>
                          {getStatusBadge(trace.outcome || trace.status || '')}
                          {trace.model && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                              {trace.model}
                            </span>
                          )}
                          {getProjectName(trace.project_id) && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                              {getProjectName(trace.project_id)}
                            </span>
                          )}
                          {(trace as any).workload_name && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-300 rounded">
                              {(trace as any).workload_name}
                            </span>
                          )}
                          {trace.aggregated_tracking && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 rounded">
                              Aggregated
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            {trace.id?.slice(0, 8)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDuration(trace.duration_ms || trace.latency_ms)}
                          </span>
                          {(trace.total_tokens || trace.input_tokens) && (
                            <span className="flex items-center gap-1">
                              <Cpu className="w-3 h-3" />
                              {(trace.total_tokens || (trace.input_tokens || 0) + (trace.output_tokens || 0)).toLocaleString()} tokens
                            </span>
                          )}
                          {trace.cost && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              ${trace.cost.toFixed(4)}
                            </span>
                          )}
                          <span>{formatTimestamp(trace.created_at || trace.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationControls
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalItems={totalItems}
                pageSize={pageSize}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Sessions Tab Content
const SessionsContent = ({ onSessionClick, sharedDateRange, onDateRangeChange }: { onSessionClick: (id: string) => void; sharedDateRange: DateRangeState | null; onDateRangeChange: (range: DateRangeState) => void }) => {
  const { selectedProjectId, projects } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Dedicated filters - aligned with backend API (user_id, agent_name, date range)
  // Initialize with shared date range for persistence
  const [filters, setFilters] = useState({
    userId: '',
    agentName: '',
    startDate: sharedDateRange?.startDate || '',
    endDate: sharedDateRange?.endDate || ''
  });

  // Time range preset for quick selection - use shared preset if available
  const [timePreset, setTimePreset] = useState(sharedDateRange?.preset || 'last_7_days');

  // Sync with shared date range when it changes (from other tabs)
  useEffect(() => {
    if (sharedDateRange) {
      setTimePreset(sharedDateRange.preset || 'last_7_days');
      if (sharedDateRange.preset === 'custom') {
        setFilters(prev => ({
          ...prev,
          startDate: sharedDateRange.startDate || '',
          endDate: sharedDateRange.endDate || ''
        }));
      }
    }
  }, [sharedDateRange]);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Calculate date range from preset
  const getDateRangeFromPreset = (preset: string) => {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    switch (preset) {
      case 'today':
        return { startDate: today.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
      case 'last_7_days': {
        const week = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        return { startDate: week.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
      }
      case 'last_30_days': {
        const month = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        return { startDate: month.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
      }
      case 'last_90_days': {
        const quarter = new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000);
        return { startDate: quarter.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
      }
      case 'this_year': {
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return { startDate: yearStart.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
      }
      case 'custom':
        return { startDate: filters.startDate, endDate: filters.endDate };
      case 'all_time':
      default:
        return { startDate: '', endDate: '' };
    }
  };

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: pageSize,
        offset: (page - 1) * pageSize
      };

      if (filters.userId) params.user_id = filters.userId;
      if (filters.agentName) params.agent_name = filters.agentName;

      // Apply time filter
      const dateRange = getDateRangeFromPreset(timePreset);
      if (dateRange.startDate) params.start_date = dateRange.startDate;
      if (dateRange.endDate) params.end_date = dateRange.endDate;

      const data = await getSessions(params);
      setSessions(data.data || data || []);
      setTotalItems(data.total || data.count || (data.data || data || []).length);
      setTotalPages(Math.ceil((data.total || data.count || (data.data || data || []).length) / pageSize));
    } catch (err: unknown) {
      console.error('Error loading sessions:', err);
      setError((err instanceof Error ? err.message : String(err)) || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.userId, filters.agentName, timePreset, filters.startDate, filters.endDate, page, pageSize, selectedProjectId, filterType, filterId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const handleTimePresetChange = (preset: string) => {
    setTimePreset(preset);
    setPage(1);
    // Propagate to parent for persistence across tabs
    const dateRange = getDateRangeFromPreset(preset);
    onDateRangeChange?.({ preset, startDate: dateRange.startDate, endDate: dateRange.endDate });
  };

  const applyFilters = () => {
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ userId: '', agentName: '', startDate: '', endDate: '' });
    setTimePreset('last_7_days');
    setPage(1);
    // Reset shared date range to default
    onDateRangeChange?.({ preset: 'last_7_days', startDate: '', endDate: '' });
  };

  const filteredSessions = sessions.filter(session => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      session.session_id?.toLowerCase().includes(searchLower) ||
      session.user_id?.toLowerCase().includes(searchLower) ||
      session.id?.toLowerCase().includes(searchLower) ||
      session.agent_name?.toLowerCase().includes(searchLower)
    );
  });

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.size === filteredSessions.length ? new Set() : new Set(filteredSessions.map((x) => x.id || x.session_id || ''))
    );
  }, [filteredSessions]);

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!globalThis.confirm(`Delete ${count} session${count > 1 ? 's' : ''}? They will be excluded from the list and metrics.`)) return;
    try {
      await bulkDeleteSessions(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadSessions();
    } catch (error: unknown) {
      console.error('Error deleting sessions:', error);
    }
  };

  const formatTimestamp = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms: number | null | undefined) => {
    if (!ms && ms !== 0) return 'N/A';
    if (ms < 1000) {
      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(ms)}ms`;
    }
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.round((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const totalSessions = sessions.length;
  const totalMessagesAcrossSessions = sessions.reduce((sum, s) => sum + (s.message_count || s.trace_count || 0), 0);
  const avgMessagesPerSession = totalSessions > 0 ? totalMessagesAcrossSessions / totalSessions : 0;
  const uniqueUsers = new Set(sessions.filter(s => s.user_id).map(s => s.user_id)).size;

  const formatNumber = (num: number | null | undefined) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  return (
    <div>
      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalSessions)}
              </p>
            </div>
            <List className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Messages</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(totalMessagesAcrossSessions)}
              </p>
            </div>
            <Activity className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg Messages/Session</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {avgMessagesPerSession.toFixed(1)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Unique Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatNumber(uniqueUsers)}
              </p>
            </div>
            <User className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation" className="flex items-center">
            <button
              onClick={toggleSelectAll}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {selectedIds.size === filteredSessions.length && filteredSessions.length > 0
                ? <CheckSquare className="w-5 h-5 text-blue-600" />
                : <Square className="w-5 h-5 text-gray-400" />
              }
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>
          {/* Time Range Filter */}
          <div className="min-w-[150px]">
            <select
              value={timePreset}
              onChange={(e) => handleTimePresetChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="today">Today</option>
              <option value="last_7_days">Last 7 days</option>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_90_days">Last 90 days</option>
              <option value="this_year">This year</option>
              <option value="all_time">All time</option>
              <option value="custom">Custom range</option>
            </select>
          </div>
          {/* Custom date range inputs */}
          {timePreset === 'custom' && (
            <>
              <div className="min-w-[130px]">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <span className="text-gray-500">to</span>
              <div className="min-w-[130px]">
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
            </>
          )}
          <div className="flex-1 min-w-[150px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-sm"
          >
            <Filter className="w-4 h-4" />
            {showFilters ? 'Hide' : 'More'}
          </button>
          <button
            onClick={loadSessions}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
        {/* Advanced filters panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-4">
              <div className="min-w-[150px]">
                <label htmlFor="obs-user-id" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">User ID</label>
                <input
                                                                                  id="obs-user-id"
                  type="text"
                  placeholder="Filter by user..."
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div className="min-w-[150px]">
                <label htmlFor="obs-agent-name" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Agent Name</label>
                <input
                                                                                  id="obs-agent-name"
                  type="text"
                  placeholder="Filter by agent..."
                  value={filters.agentName}
                  onChange={(e) => setFilters({ ...filters, agentName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={applyFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Apply
                </button>
                {(filters.userId || filters.agentName || timePreset !== 'last_7_days') && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm"
                  >
                    Clear All
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <ProjectAssignmentSelect
              bulk
              currentProjectId={null}
              onAssign={async (projectId) => {
                await bulkAssignSessionProject(Array.from(selectedIds), projectId);
                setSessions(prev => prev.map(s => selectedIds.has(s.id || s.session_id || '') ? { ...s, project_id: projectId } : s));
                setSelectedIds(new Set());
              }}
              size="sm"
            />
            <WorkloadAssignmentSelect
              bulk
              onAssign={async (workloadId) => {
                const ids = Array.from(selectedIds);
                await bulkAssignSessionWorkload(ids, workloadId);
                setSelectedIds(new Set());
                loadSessions();
              }}
              size="sm"
            />
            <button
              type="button"
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Sessions List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        {filteredSessions.length === 0 ? (
          <div className="p-12 text-center">
            <List className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 dark:text-gray-400">No sessions found</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredSessions.map((session) => {
                const messageCount = session.message_count || session.trace_count || 0;
                const sessionDuration = session.duration_ms || session.total_duration_ms;
                const sessionCost = session.total_cost || session.cost;

                return (
                  <div
                    key={session.id || session.session_id || ''}
                    onClick={() => onSessionClick(session.id || session.session_id || '' || '')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSessionClick(session.id || session.session_id || '' || ''); }} role="button" tabIndex={0}
                    className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation(); }} role="presentation">
                        <button
                          onClick={() => toggleSelect(session.id || session.session_id || '')}
                          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {selectedIds.has(session.id || session.session_id || '')
                            ? <CheckSquare className="w-5 h-5 text-blue-600" />
                            : <Square className="w-5 h-5 text-gray-400" />
                          }
                        </button>
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900 dark:text-white">
                            Session {(session.id || session.session_id || '')?.slice(0, 8)}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            messageCount > 10
                              ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                              : messageCount > 5
                              ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                          }`}>
                            {messageCount} messages
                          </span>
                          {session.model && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 rounded">
                              {session.model}
                            </span>
                          )}
                          {session.agent_name && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-300 rounded flex items-center gap-1">
                              <Bot className="w-3 h-3" />
                              {session.agent_name}
                            </span>
                          )}
                          {(() => {
                            const pName = session.project_id ? projects.find(p => p.id === String(session.project_id))?.name : null;
                            return pName ? (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded">
                                {pName}
                              </span>
                            ) : null;
                          })()}
                          {(session as any).workload_name && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-300 rounded">
                              {(session as any).workload_name}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {session.user_id && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {session.user_id}
                            </span>
                          )}
                          {sessionDuration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(sessionDuration)}
                            </span>
                          )}
                          {session.total_tokens && (
                            <span className="flex items-center gap-1">
                              <Cpu className="w-3 h-3" />
                              {session.total_tokens.toLocaleString()} tokens
                            </span>
                          )}
                          {sessionCost && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              ${sessionCost.toFixed(4)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatTimestamp(session.created_at || session.started_at)}
                          </span>
                        </div>
                      </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Mini activity indicator */}
                        {messageCount > 0 && (
                          <div className="hidden md:flex items-center gap-0.5">
                            {[...Array(Math.min(messageCount, 8))].map((_, i) => (
                              <div
                                key={`bar-${i}`}
                                className="w-1.5 h-4 bg-blue-200 dark:bg-blue-700 rounded-sm"
                                style={{
                                  height: `${Math.random() * 12 + 8}px`
                                }}
                              />
                            ))}
                            {messageCount > 8 && (
                              <span className="text-xs text-gray-400 ml-1">+{messageCount - 8}</span>
                            )}
                          </div>
                        )}
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <PaginationControls
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalItems={totalItems}
                pageSize={pageSize}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ObservabilityPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'traces';

  // Shared date range state for filter persistence across tabs
  const [sharedDateRange, setSharedDateRange] = useState<DateRangeState>({
    preset: 'last_7_days',
    startDate: '',
    endDate: ''
  });

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const handleTraceClick = (traceId: string) => {
    navigate(`/ai-operations/traces/${traceId}`);
  };

  const handleSessionClick = (sessionId: string) => {
    navigate(`/ai-operations/sessions/${sessionId}`);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
          Observability
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 transition-colors">
          Monitor traces and sessions to understand system behavior and debug issues
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'traces' && (
        <TracesContent
          onTraceClick={handleTraceClick}
          sharedDateRange={sharedDateRange}
          onDateRangeChange={setSharedDateRange}
        />
      )}
      {activeTab === 'sessions' && (
        <SessionsContent
          onSessionClick={handleSessionClick}
          sharedDateRange={sharedDateRange}
          onDateRangeChange={setSharedDateRange}
        />
      )}
    </div>
  );
};

export default ObservabilityPage;
